import React from 'react';
import { Checkbox } from 'antd';
import styles from './table.module.scss';
import RowActionsMenu, { ROW_ACTIONS_COLUMN_WIDTH } from './RowActionsMenu';
import PropTypes from 'prop-types';

const GenericTable = ({
  data = [],
  columns = [],
  actions = [],
  selectable = false,
  selectedRowKeys = [],
  onSelectionChange,
  emptyState = {
    icon: '/assets/icons/no-users.svg',
    title: 'Trenutno nema podataka',
  },
  rowKey = '_id',
  rowClassName,
  tableMinWidth = null,
  wrapperClassName = '',
  fixedLayout = false,
}) => {
  const getRowId = (row) => row?.[rowKey] || row?._id;
  const selectableRows = data
    .map((row) => getRowId(row))
    .filter((id) => id !== undefined && id !== null);
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((id) => selectedRowKeys.includes(id));
  const hasSomeSelected =
    selectableRows.length > 0 &&
    selectableRows.some((id) => selectedRowKeys.includes(id));

  const handleSelectAll = (checked) => {
    if (typeof onSelectionChange !== 'function') return;
    if (checked) {
      const merged = [...new Set([...selectedRowKeys, ...selectableRows])];
      onSelectionChange(merged);
      return;
    }

    const filtered = selectedRowKeys.filter((id) => !selectableRows.includes(id));
    onSelectionChange(filtered);
  };

  const toggleRowSelection = (id, checked) => {
    if (id === undefined || id === null || typeof onSelectionChange !== 'function') return;
    if (checked) {
      onSelectionChange([...new Set([...selectedRowKeys, id])]);
      return;
    }
    onSelectionChange(selectedRowKeys.filter((rowId) => rowId !== id));
  };

  return (
    <div className={['table-wrapper', wrapperClassName].filter(Boolean).join(' ')}>
      <table
        style={{
          width: '100%',
          minWidth: tableMinWidth || undefined,
          tableLayout: fixedLayout ? 'fixed' : undefined,
        }}
        className={styles.table}
      >
        <thead>
          <tr>
            {selectable && (
              <th style={{ width: 44 }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && hasSomeSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                className={col.align}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.label}
              </th>
            ))}
            {actions.length > 0 && (
              <th
                className="table-actions-cell"
                style={{ width: ROW_ACTIONS_COLUMN_WIDTH }}
              />
            )}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
              key={getRowId(row)}
              className={
                typeof rowClassName === 'function'
                  ? rowClassName(row)
                  : rowClassName || undefined
              }
            >
              {selectable && (
                <td>
                  <Checkbox
                    checked={selectedRowKeys.includes(getRowId(row))}
                    onChange={(e) =>
                      toggleRowSelection(getRowId(row), e.target.checked)
                    }
                  />
                </td>
              )}
              {columns.map((col) => (
                <td key={col.key} className={col.align}>
                  {typeof col.customCell === 'function'
                    ? col.customCell(row)
                    : typeof col.render === 'function'
                      ? col.render(row[col.key], row)
                      : row[col.key]}
                </td>
              ))}
              {actions.length > 0 && (
                <td className="table-actions-cell">
                  <RowActionsMenu actions={actions} row={row} />
                </td>
              )}
            </tr>
          ))}
          {data.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + (actions.length > 0 ? 1 : 0) + (selectable ? 1 : 0)}
                style={{ textAlign: 'center', padding: 8 }}
              >
                <div className={styles.noData}>
                  <img src={emptyState.icon} alt="Nema podataka" />
                  <h3>{emptyState.title}</h3>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

GenericTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func,
      customCell: PropTypes.func,
      align: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ).isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
      icon: PropTypes.node,
      className: PropTypes.string,
      danger: PropTypes.bool,
      disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      onClick: PropTypes.func.isRequired,
    })
  ),
  selectable: PropTypes.bool,
  selectedRowKeys: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onSelectionChange: PropTypes.func,
  emptyState: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    actionButton: PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      className: PropTypes.string,
      type: PropTypes.string,
      style: PropTypes.object,
    }),
  }),
  rowKey: PropTypes.string,
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  tableMinWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  wrapperClassName: PropTypes.string,
  fixedLayout: PropTypes.bool,
};

GenericTable.defaultProps = {
  actions: [],
  selectable: false,
  selectedRowKeys: [],
  onSelectionChange: undefined,
  emptyState: {
    icon: '/assets/icons/no-users.svg',
    title: 'Trenutno nema podataka',
  },
  rowKey: '_id',
  rowClassName: undefined,
  tableMinWidth: null,
  wrapperClassName: '',
  fixedLayout: false,
};

export default GenericTable;
